import {
  Box,
  Button,
  Container,
  Field,
  Flex,
  Heading,
  Input,
  NativeSelect,
  Text,
  VStack
} from "@chakra-ui/react"
import { useState } from "react"

type FormData = {
  email: string
  name: string
  whoAreYou: string
  propertiesCount: string
  capitalPerYear: string
  howDidYouHear: string
}

const initialForm: FormData = {
  email: "",
  name: "",
  whoAreYou: "",
  propertiesCount: "",
  capitalPerYear: "",
  howDidYouHear: ""
}

const whoOptions = [
  "Property Developer",
  "Property Investor/Asset Manager",
  "Social Landlord",
  "Housing Association",
  "Local Authority",
  "Other"
]
const propertiesOptions = ["1", "2–5", "5–10", "25+"]
const capitalOptions = ["£1–50k", "£50–100k", "£100–500k", "£1M+"]

const labelProps = {
  color: "whiteAlpha.800",
  fontSize: "sm",
  fontWeight: "medium"
} as const

const ChoiceGroup = ({
  options,
  value,
  onChange,
  hasError
}: {
  options: string[]
  value: string
  onChange: (v: string) => void
  hasError?: boolean
}) => (
  <Box>
    <Flex gap={2} flexWrap="wrap">
      {options.map((opt) => (
        <Button
          key={opt}
          type="button"
          size="sm"
          onClick={() => onChange(opt)}
          bg={value === opt ? "white" : "transparent"}
          color={value === opt ? "brand.700" : "white"}
          borderWidth="1px"
          borderColor={
            hasError && !value
              ? "red.400"
              : value === opt
                ? "white"
                : "whiteAlpha.500"
          }
          _hover={{ bg: value === opt ? "white" : "whiteAlpha.200" }}
          fontWeight={value === opt ? "bold" : "normal"}
        >
          {opt}
        </Button>
      ))}
    </Flex>
    {hasError && !value && (
      <Text color="red.300" fontSize="xs" mt={1}>
        Please select one
      </Text>
    )}
  </Box>
)

interface CallToActionProps {
  title?: string
  description?: string
}

const DEFAULT_TITLE = "See the retrofit potential hiding in plain sight."
const DEFAULT_DESCRIPTION =
  "GreenFlip identifies the best energy upgrade opportunities across your portfolio, with a financial case for each one."

const CallToAction: React.FC<CallToActionProps> = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION
}) => {
  const [form, setForm] = useState<FormData>(initialForm)
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const set = (field: keyof FormData) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitAttempted(true)

    if (!form.whoAreYou || !form.propertiesCount || !form.capitalPerYear) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(false)
    try {
      const res = await fetch("https://formspree.io/f/xrejjrra", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          email: form.email,
          name: form.name,
          "Who are you?": form.whoAreYou,
          "Properties count": form.propertiesCount,
          "Capital per year": form.capitalPerYear,
          "How did you hear about us?": form.howDidYouHear || undefined
        })
      })
      if (res.ok) {
        setSubmitted(true)
      } else {
        setSubmitError(true)
      }
    } catch {
      setSubmitError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box id="early-access" bg="brand.700">
      <Container maxW="container.xl" py={20} textAlign="center">
        <Heading as="h2" size="4xl" color="white" pb={4}>
          {title}
        </Heading>
        <Text m="auto" pb={10} maxW={800} color="white" fontSize="xl">
          {description}
        </Text>

        {submitted ? (
          <Box
            m="auto"
            maxW={560}
            bg="whiteAlpha.200"
            borderRadius="2xl"
            p={12}
          >
            <Text color="white" fontSize="xl" fontWeight="medium">
              You're on the list. We will be in touch as soon as possible.
            </Text>
          </Box>
        ) : (
          <Box
            m="auto"
            maxW={560}
            bg="whiteAlpha.100"
            borderRadius="2xl"
            p={[6, 10]}
            textAlign="left"
          >
            <form onSubmit={handleSubmit} noValidate>
              <VStack gap={6} align="stretch">
                <Field.Root>
                  <Field.Label {...labelProps}>Email address</Field.Label>
                  <Input
                    type="email"
                    placeholder="name@example.com"
                    value={form.email}
                    onChange={(e) => set("email")(e.target.value)}
                    required
                    bg="whiteAlpha.200"
                    borderColor="whiteAlpha.400"
                    color="white"
                    _placeholder={{ color: "whiteAlpha.400" }}
                    _focus={{ borderColor: "white", bg: "whiteAlpha.300" }}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label {...labelProps}>Your name</Field.Label>
                  <Input
                    placeholder="Jane Smith"
                    value={form.name}
                    onChange={(e) => set("name")(e.target.value)}
                    required
                    bg="whiteAlpha.200"
                    borderColor="whiteAlpha.400"
                    color="white"
                    _placeholder={{ color: "whiteAlpha.400" }}
                    _focus={{ borderColor: "white", bg: "whiteAlpha.300" }}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label {...labelProps}>Who are you?</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={form.whoAreYou}
                      onChange={(e) => set("whoAreYou")(e.target.value)}
                      bg="whiteAlpha.200"
                      borderColor={
                        submitAttempted && !form.whoAreYou
                          ? "red.400"
                          : "whiteAlpha.400"
                      }
                      color={form.whoAreYou ? "white" : "whiteAlpha.400"}
                      _focus={{ borderColor: "white", bg: "whiteAlpha.300" }}
                    >
                      <option value="" disabled style={{ color: "#000" }}>
                        Select an option
                      </option>
                      {whoOptions.map((opt) => (
                        <option key={opt} value={opt} style={{ color: "#000" }}>
                          {opt}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator color="white" />
                  </NativeSelect.Root>
                  {submitAttempted && !form.whoAreYou && (
                    <Text color="red.300" fontSize="xs" mt={1}>
                      Please select your role
                    </Text>
                  )}
                </Field.Root>

                <Field.Root>
                  <Field.Label {...labelProps}>
                    How many properties does your organisation own or manage?
                  </Field.Label>
                  <ChoiceGroup
                    options={propertiesOptions}
                    value={form.propertiesCount}
                    onChange={set("propertiesCount")}
                    hasError={submitAttempted}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label {...labelProps}>
                    How much capital do you deploy per year?
                  </Field.Label>
                  <ChoiceGroup
                    options={capitalOptions}
                    value={form.capitalPerYear}
                    onChange={set("capitalPerYear")}
                    hasError={submitAttempted}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label {...labelProps}>
                    How did you hear about us?{" "}
                    <Box as="span" fontWeight="normal" color="whiteAlpha.600">
                      (optional)
                    </Box>
                  </Field.Label>
                  <Input
                    placeholder="e.g. LinkedIn, a colleague, an event"
                    value={form.howDidYouHear}
                    onChange={(e) => set("howDidYouHear")(e.target.value)}
                    bg="whiteAlpha.200"
                    borderColor="whiteAlpha.400"
                    color="white"
                    _placeholder={{ color: "whiteAlpha.400" }}
                    _focus={{ borderColor: "white", bg: "whiteAlpha.300" }}
                  />
                </Field.Root>

                <Button
                  type="submit"
                  bg="white"
                  color="brand.700"
                  size="lg"
                  w="full"
                  mt={2}
                  loading={isSubmitting}
                  _hover={{ bg: "gray.100" }}
                  fontWeight="bold"
                >
                  Sign up for early access
                </Button>

                {submitError && (
                  <Text color="red.300" fontSize="sm" textAlign="center">
                    Your submission didn't go through. Check your connection and
                    try again, or email us at hello@greenflip.co.
                  </Text>
                )}
              </VStack>
            </form>
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default CallToAction
